import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { evFacetClick } from 'events/search';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { FILTERS_RE } from 'common/regex';
import { trackEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';
import useMartyContext from 'hooks/useMartyContext';
import useWindowSize from 'hooks/useWindowSize';
import { firePixelEvent } from 'actions/pixelServer';
import ToggleButton from 'components/common/ToggleButton';
import Tag from 'components/icons/Tag';
import Tooltip from 'components/common/Tooltip';
import { SEARCH_AUTO_FACET_TOOLTIP_SEEN_COOKIE } from 'constants/cookies';
import { setCookie } from 'helpers';

import css from 'styles/components/search/compactSingleSelects.scss';

const ASSIST_FILTERS_INFO_COPY = 'We applied filters based on your awesome search. Hope it helps!';

export const CompactSingleSelects = ({
  filters,
  className,
  hasAutoScroll,
  removePersonalizedSize,
  handlePillClick,
  clearFilters,
  isHydraSearchBarDropDown,
  autoFacetCookie,
  autoFacetToolTipSeenCookie,
  handleAutoFacetToggle,
  isShowAutoFacetToggle
}) => {
  const {
    testId,
    router,
    marketplace: {
      search: { maxPills },
      cookieDomain
    }
  } = useMartyContext();
  const breadcrumbWrapper = useRef();
  const [scrollingBreadcrumbs, setScrollingBreadcrumbs] = useState(false);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [isAssistFiltersTooltipVisible, setAssistFiltersTooltipVisible] = useState(true);
  const { width: windowWidth } = useWindowSize();
  const { scrollLeft, scrollWidth, clientWidth } = breadcrumbWrapper.current || {};
  const maxScroll = scrollWidth - clientWidth;
  const selectionTitle = filters.breadcrumbs.length ? 'Your Selections' : 'Suggested Filters';
  const hasAssistedFilters = filters?.breadcrumbs.find(b => b.autoFaceted) ? true : false;

  useEffect(() => {
    // set a stale cookie for tooltip seen so we only show it once
    if (autoFacetToolTipSeenCookie !== true && hasAssistedFilters) {
      setCookie(SEARCH_AUTO_FACET_TOOLTIP_SEEN_COOKIE, true, { domain: cookieDomain });
    }
  }, []);

  useEffect(() => {
    if (windowWidth && breadcrumbWrapper.current) {
      setShowScrollButtons(hasAutoScroll && scrollWidth - clientWidth > 0);
    }
    firePixelEvent('search');
  }, [windowWidth, hasAutoScroll, scrollWidth, clientWidth]);

  if (!filters.breadcrumbs.length && !filters?.pills?.recommendations?.length) {
    return null;
  }

  const makeBreadcrumbClickHandler = crumb => e => {
    e.preventDefault();
    if (filters?.personalizedSize?.sizes?.includes(crumb.name)) {
      removePersonalizedSize();
    }
    router.forceBrowserPush(crumb.removeUrl);
    track(() => [
      evFacetClick,
      {
        breadcrumbRemove: crumb,
        facetClickSource: 'FACET_PILLS',
        selected: false,
        deselected: true
      }
    ]);
  };

  const makeCrumbContent = (crumb, isSingleSelect) => {
    const crumbTitle = `Remove ${crumb.name} filter`;
    const showAutoFacet = autoFacetCookie !== 'off' && crumb.autoFaceted && isShowAutoFacetToggle;
    return (
      <a // eslint-disable-line jsx-a11y/anchor-is-valid
        className={cn({ [css.productTypeClassifierPill]: showAutoFacet })}
        href={!FILTERS_RE.test(crumb.removeUrl) ? crumb.removeUrl : ''}
        aria-label={crumbTitle}
        data-singleselect={isSingleSelect}
        tabIndex={0}
        onClick={makeBreadcrumbClickHandler(crumb)}
        data-test-id={testId('singleSelectBreadcrumb')}
      >
        {showAutoFacet && <Tag className={css.tag} />}
        {crumb.name}
      </a>
    );
  };

  const onPillClick = (crumb, index) => {
    if (breadcrumbWrapper.current) {
      breadcrumbWrapper.current.scrollLeft = 0;
    }

    handlePillClick(crumb, index);
    trackEvent('TE_SEARCH_PILLCLICKED', `${crumb.name}:${crumb.value}`);
    track(() => [
      evFacetClick,
      {
        crumb,
        facetClickSource: 'FACET_PILLS',
        selected: true,
        deselected: false
      }
    ]);
  };

  const makePillMarkup = () => {
    const breadCrumbs = filters.breadcrumbs.map(crumb => {
      const { name, removeUrl } = crumb;
      const isSingleSelect = Object.keys(filters.selected.singleSelects).some(e => filters.selected.singleSelects[e][0] === name);
      return (
        <li
          key={`${removeUrl}-${name}`}
          className={cn({
            [css.onSaleFacet]: name === 'On Sale',
            [css.defaultTerm]: name === 'Search'
          })}
        >
          {makeCrumbContent(crumb, isSingleSelect)}
        </li>
      );
    });

    const makePills = filters?.pills?.recommendations.map((crumb, i) => {
      if (i < maxPills + 1) {
        return (
          <li key={`${crumb.value}-${crumb.name}`} className={css.pills}>
            <button type="button" key={i} onClick={() => onPillClick(crumb, i)} aria-label={`Select ${crumb.value} filter`}>
              {crumb.value}
            </button>
          </li>
        );
      }
    });
    return breadCrumbs.concat(makePills);
  };

  const scrollBreadCrumbs = (isScrollingLeft, scrollTo) => {
    if (breadcrumbWrapper.current) {
      const {
        current: { scrollLeft }
      } = breadcrumbWrapper;
      setScrollingBreadcrumbs(true);
      if ((isScrollingLeft && scrollLeft > scrollTo) || (!isScrollingLeft && scrollLeft < scrollTo)) {
        breadcrumbWrapper.current.scrollLeft += isScrollingLeft ? -20 : 20;
        setTimeout(scrollBreadCrumbs, 10, isScrollingLeft, scrollTo);
      } else {
        setScrollingBreadcrumbs(false);
      }
    }
  };

  const onScrollBreadcrumbs = e => {
    const {
      current: { scrollLeft, clientWidth }
    } = breadcrumbWrapper;

    if (scrollingBreadcrumbs) {
      e.preventDefault();
    } else {
      const isScrollingLeft = e.target.className === css.leftScroll;
      let scrollTo = scrollLeft + clientWidth < maxScroll ? scrollLeft + clientWidth : maxScroll;

      if (isScrollingLeft) {
        scrollTo = scrollLeft - clientWidth > 0 ? scrollLeft - clientWidth : 0;
      }

      scrollBreadCrumbs(isScrollingLeft, scrollTo);
    }
  };

  const closeAssistFiltersToolTip = () => {
    setAssistFiltersTooltipVisible(false);
  };

  return (
    <div className={css.container}>
      <div
        className={cn(css.compactSingleSelects, className || null, {
          [css.hasAutoScroll]: hasAutoScroll,
          [css.pillsCompactSingleSelects]: hasAutoScroll
        })}
        data-test-id={testId('breadcrumbScroll')}
      >
        <div className={css.headerContainer}>
          {isShowAutoFacetToggle ? (
            <ToggleButton
              id="assistFiltersDesktop"
              handleToggle={handleAutoFacetToggle}
              label="Assisted Filters"
              infoCopy={ASSIST_FILTERS_INFO_COPY}
              isToggledDefault={autoFacetCookie !== 'off'}
            />
          ) : (
            <h2 className={css.selectionText} data-test-id={testId('breadcrumbTitle')}>
              {selectionTitle}
            </h2>
          )}
          {isHydraSearchBarDropDown && filters?.breadcrumbs?.length > 0 && !isShowAutoFacetToggle && (
            <div className={css.filterTextContainer}>
              <div className={css.filtersText}>Filters were included based on your selections.</div>
              <button type="button" className={css.clearFiltersBtn} onClick={clearFilters}>
                Clear all filters
              </button>
            </div>
          )}
        </div>
        {showScrollButtons && (
          <button
            type="button"
            onMouseDown={onScrollBreadcrumbs}
            tabIndex="-1"
            disabled={scrollLeft <= 0}
            className={css.leftScroll}
            data-test-id={testId('leftBreadcrumbScroll')}
          >
            Left Scroll
          </button>
        )}
        <ul id="searchSelectedFilters" ref={breadcrumbWrapper} data-test-id={testId('breadcrumbContainer')}>
          {makePillMarkup()}
        </ul>
        {showScrollButtons && (
          <button
            type="button"
            onMouseDown={onScrollBreadcrumbs}
            tabIndex="-1"
            disabled={scrollLeft === maxScroll}
            className={css.rightScroll}
            data-test-id={testId('rightBreadcrumbScroll')}
          >
            Right Scroll
          </button>
        )}
      </div>
      {isShowAutoFacetToggle && hasAssistedFilters && (
        <>
          <button type="button" className={css.clearFiltersInlineBtn} onClick={clearFilters}>
            Clear all filters
          </button>
          {autoFacetToolTipSeenCookie !== 'true' && (
            <Tooltip
              tooltipId="tooltipAssistFiltersSearch"
              wrapperClassName={css.tooltipWrapper}
              tooltipClassName={css.tooltip}
              contentClassName={css.tooltipContent}
              direction="bottom"
              content={
                <div className={css.assistContent}>
                  {ASSIST_FILTERS_INFO_COPY}
                  <button type="button" value="close" className={css.close} aria-label="Close" onClick={() => closeAssistFiltersToolTip()}></button>
                </div>
              }
              clickable={true}
              clickOpen={isAssistFiltersTooltipVisible}
              useOverlay={false}
            ></Tooltip>
          )}
        </>
      )}
    </div>
  );
};

export default withErrorBoundary('CompactSingleSelects', CompactSingleSelects);
